import { Color, DirectionalLight, Group, MathUtils, Vector3 } from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { gsap } from 'gsap';

import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import DebugPane from '../_app/cuchillo/3D/debug-utils/DebugPane';
import WaterPlane, {
  WATER_SETTINGS,
  WATER_SETTINGS_DESKTOP,
  WATER_SETTINGS_PORTRAIT,
} from './WaterPlane';
import LiquidSphere, { sphereIsMobile } from './LiquidSphere';
import Fireflies, { FIREFLY_SETTINGS } from './Fireflies';
import OxigenParticles from './OxigenParticles';
import StarField from './StarField';
import Clouds from './Clouds';
import HorizonHaze from './HorizonHaze';
import CelestialBody, { SUN_BODY_SETTINGS, MOON_SETTINGS } from './CelestialBody';
import SceneCompositor, { BRAND_SETTINGS, RENDER_SETTINGS, BLOOM_SETTINGS } from './SceneCompositor';
import TimeOfDay, { TIME_SETTINGS } from './TimeOfDay';
import { isDebug, isMobile, isPortrait } from '../_app/cuchillo/core/Basics';

// Colors del reflex especular del mar: sol càlid (dia), lluna fred (nit).
const _sunGlint = new Color(0xbfe0ff); // reflex del sol al mar → clar brillant (més llums; el grade el porta cap a #0165bb)
const _moonGlint = new Color(0xbfe0ff);

// ── Layers del double render ───────────────────────────────────────────────────
// SEA (mar+cel) va al render de DALT; la resta al render de BAIX (2n render).
export const SEA_LAYER = 0;
export const SPHERE_LAYER = 6;
export const PARTICLE_LAYER = 7; // partícules generals → 2n render (underwater)
export const GODRAY_LAYER = 8; // godrays (fake, per bloc) → 2n render (underwater)
export const LIGHTCIRCLE_LAYER = 9; // cercles de llum (block_step) → 2n render (underwater)
export const FIREFLY_LAYER = 10; // lluernes bioluminescents → 2n render (underwater) + bloom
export const SEABED_LAYER = 11; // fons marí del footer (Home/About) → 2n render (underwater)

// Cel base (els paràmetres els sobreescriu el sistema d'hores; aquí només l'escala i el default).
export const SKY_SETTINGS = { scale: 12000, exposure: 0.18 };

// true  → animació anterior (framing + sea settings + scroll framing/seaY)
// false → només reframe Y (intro 30→-10, scroll -10→-20)
export const USE_LEGACY_SEA_ANIM = true;

// ── ANIMACIÓ D'ENTRADA de la Home (1r cop) ──────────────────────────────────────
//   · framing → reenquadrament vertical del cel+mar (seaFramingY): from → to
//   · sea     → settings del mar: de SEA_INTRO_FROM cap als valors actuals (repòs/pane)
const INTRO_TIMING_LEGACY = {
  framing: { from: 30, to: -10, duration: 3.75, delay: 0, ease: 'power1.inout' },
  sea: { duration: 8.5, delay: 1, ease: 'power3.out' },
};
const INTRO_TIMING_SIMPLE = {
  framing: { from: 30, to: -10, duration: 2.75, delay: 0, ease: 'power3.out' },
};
export const INTRO_TIMING = USE_LEGACY_SEA_ANIM ? INTRO_TIMING_LEGACY : INTRO_TIMING_SIMPLE;

// MOVE del billboard: progress (start→end) → posició concreta a `end` (additiu sobre post-intro).
// const SEA_SCROLL_LEGACY = {
//   start: 0.5,
//   end: 1.0,
//   framingTarget: -5,
//   seaYTarget: 0.111111, // VERT (fracció × HEIGHT; ≈100px @900) — responsive
// };
const SEA_SCROLL_DESKTOP = {
  start: 0.5,
  end: 1.0,
  framingTarget: -5,
  seaYTarget: 0.111111,
};

const SEA_SCROLL_PORTRAIT = {
  start: 0.5,
  end: 1.0,
  // framingTarget: -8,
  // seaYTarget: 0.22,
  framingTarget: -3,   // scroll molt més lleuger a mobile (abans -8)
  seaYTarget: -0.05,   // el mar amb prou feines puja amb l'scroll: repòs -0.1 → -0.05 (abans 0.16)
};

// const SEA_SCROLL_SIMPLE = {
//   start: 0.5,
//   end: 1.0,
//   framingTarget: -20, // post-intro (-10) → -20
// };
// export const SEA_SCROLL = USE_LEGACY_SEA_ANIM ? SEA_SCROLL_LEGACY : SEA_SCROLL_SIMPLE;
export const SEA_SCROLL = isPortrait ? SEA_SCROLL_PORTRAIT : SEA_SCROLL_DESKTOP;

// Petit fade del SURFACE (1r render, cel+mar) a l'entrada (lligat al framing = 1r show). from → 1.
export const SURFACE_FADE = { from: 1, duration: 1.5, ease: 'power2.out' };

// Escena 3D PERSISTENT del projecte: present sempre a tota la web. init() es crida un cop des de
// main.js i start() la pren el primer cop des de pages/Default. Munta mar, cel, sol/lluna,
// partícules, i fa el DOUBLE RENDER via SceneCompositor. L'esfera de líquid la munta el bloc que en té
// el target (addSphere/removeSphere).
export default class Scene {
  static group = new Group();
  static _water = null;
  static _sky = null;
  static _moon = null;
  static _sunBody = null;
  static _moonBody = null;
  static _liquid = null;
  static _fireflies = null;
  static _sphereTarget = null;
  static _sphereViewportCenter = false;
  static _stars = null;
  static _particles = null;
  static _clouds = null;
  static _haze = null;
  static _compositor = null;
  static _running = false;
  static _initialized = false;
  static _sunDir = new Vector3();
  static _moonDir = new Vector3();
  static _exposure = SKY_SETTINGS.exposure;
  static _lastT = 0;
  static _godrays = []; // godrays per-bloc, registrats per l'scroll-item del bloc
  static _lightCircles = []; // cercles de llum per-bloc (block_step)
  static _seabed = null; // fons marí del footer (3D/seabed), registrat per l'scroll-item del footer
  static seabedEnabled = false; // les pàgines amb escena (Home/About) l'activen a _sceneBeforeShow; Default el desactiva
  static _seaProgressStart = null;

  static init(_container) {
    if (this._initialized) return;
    if (!WebGLSketch.renderer) WebGLSketch.init({ antialias: !sphereIsMobile() }); // el canvas només rep un quad (composite): l'MSAA no aporta res a mobile
    WebGLSketch.scene.add(this.group);

    // ── Sky (render de dalt) ───────────────────────────────────────────────────
    this._sky = new Sky();
    this._sky.scale.setScalar(SKY_SETTINGS.scale);
    this._sky.layers.set(SEA_LAYER);
    this.group.add(this._sky);

    // ── Llum direccional (sol/lluna) — no il·lumina materials, es manté per compat ──
    this._moon = new DirectionalLight(0xbcd0ff, 1.2);
    this._moon.layers.set(SEA_LAYER);
    this.group.add(this._moon);
    this.group.add(this._moon.target);

    // ── Sol i Lluna (billboards visibles, independents) ─────────────────────────
    this._sunBody = new CelestialBody(SUN_BODY_SETTINGS);
    const sunMesh = this._sunBody.init();
    this._sunBody.setLayer(SEA_LAYER);
    this.group.add(sunMesh);

    this._moonBody = new CelestialBody(MOON_SETTINGS);
    const moonMesh = this._moonBody.init();
    this._moonBody.setLayer(SEA_LAYER);
    this.group.add(moonMesh);

    // ── Water (item, render de dalt) — mida des del canvas #scene-three ─────────
    // (el renderer/pantalla), no d'un target d'un bloc concret.
    this._water = new WaterPlane(WebGLSketch.domElement);
    const waterMesh = this._water.init();
    waterMesh.layers.set(SEA_LAYER);
    this.group.add(waterMesh);

    // ── Partícules generals (2n render underwater) ──────────────────────────────
    // Mobile: sense bombolles d'oxigen (base estable de rendiment; es reafegiran de mica en mica). No s'instancien.
    if (!isMobile) {
      this._particles = new OxigenParticles();
      const particlePoints = this._particles.init(WebGLSketch.size.y);
      this._particles.setLayer(PARTICLE_LAYER);
      this.group.add(particlePoints);
    }

    // ── Estrelles (només de nit; van al cel = render de dalt) ───────────────────
    this._stars = new StarField();
    const starPoints = this._stars.init();
    this._stars.setLayer(SEA_LAYER);
    this.group.add(starPoints);

    // ── Núvols + boirina d'horitzó (cel = render de dalt) ───────────────────────
    this._clouds = new Clouds();
    const cloudMesh = this._clouds.init();
    this._clouds.setLayer(SEA_LAYER);
    this.group.add(cloudMesh);

    this._haze = new HorizonHaze();
    const hazeMesh = this._haze.init();
    this._haze.setLayer(SEA_LAYER);
    this.group.add(hazeMesh);

    // ── Compositor (double render) ─────────────────────────────────────────────
    this._compositor = new SceneCompositor();
    this._compositor.init(WebGLSketch.renderer, WebGLSketch.size.x, WebGLSketch.size.y);

    // ── Hora inicial: real del dispositiu o fixa (per defecte 12) segons TIME_SETTINGS ──
    this.setHour(this._resolveHour());

    // ── Debug: la scene delega la construcció del pane a DebugPane ───────────────
    if (isDebug) DebugPane.mountScene(this);

    this._initialized = true;

    // El render (double render) el pren start(), que es crida des de pages/Default la primera
    // vegada. Fins llavors WebGLSketch renderitza normalment i loop() no fa res (guarda _running).
  }

  // ── Sistema d'hores ────────────────────────────────────────────────────────────
  // Aplica la configuració d'ambient d'una hora (0–24): posició sol/lluna, exposició,
  // cel, llum i colors d'aigua/reflex. Vegeu TimeOfDay.
  static setHour(hour) {
    this._lastHour = hour;
    this.applyTimeConfig(TimeOfDay.config(hour));
  }

  // Hora a aplicar segons TIME_SETTINGS: real del dispositiu o fixa (12 per defecte).
  static _resolveHour() {
    return TIME_SETTINGS.useDeviceTime ? TimeOfDay.now() : TIME_SETTINGS.fixedHour;
  }

  // Elevació/azimut (graus) → direcció unitària (camera2D: davant = azimut ~180, amunt = +Y).
  static _dirFrom(elevation, azimuth, out) {
    const phi = MathUtils.degToRad(90 - elevation);
    const theta = MathUtils.degToRad(azimuth);
    return out.setFromSphericalCoords(1, phi, theta);
  }

  static applyTimeConfig(cfg) {
    // ── SOL (dona el cel del dia via Sky). Sota l'horitzó de nit → cel fosc (grade → navy). ──
    this._elevation = cfg.sun.elevation;
    this._azimuth = cfg.sun.azimuth;
    this._dirFrom(cfg.sun.elevation, cfg.sun.azimuth, this._sunDir);
    // ── LLUNA (cos independent, amb la seva intensitat). ──
    this._dirFrom(cfg.moon.elevation, cfg.moon.azimuth, this._moonDir);

    if (this._sky) {
      const u = this._sky.material.uniforms;
      u.turbidity.value = cfg.turbidity;
      u.rayleigh.value = cfg.rayleigh;
      u.mieCoefficient.value = cfg.mieCoefficient;
      u.mieDirectionalG.value = cfg.mieDirectionalG;
      u.sunPosition.value.copy(this._sunDir);
    }
    if (this._sunBody) {
      this._sunBody.setDirection(this._sunDir);
      // El disc del sol només es veu quan és sobre l'horitzó (i escala amb la llum del dia).
      this._sunBody.setIntensity(cfg.sun.elevation > -1 ? cfg.daylight : 0);
    }
    if (this._moonBody) {
      this._moonBody.setDirection(this._moonDir);
      this._moonBody.setIntensity(cfg.moonIntensity);
    }
    if (this._stars) {
      this._stars.setOpacity(cfg.nightFactor);                       // només de nit (fade)
      this._stars.setHdrBoost(1 / Math.max(cfg.exposure, 0.005));    // compensa l'exposició del cel
    }
    if (this._water) {
      // Reflex especular del mar cap al cos DOMINANT: sol de dia (càlid), lluna de nit (fred).
      const dayDom = cfg.daylight >= cfg.moonIntensity;
      const dir = dayDom ? this._sunDir : this._moonDir;
      const gI = MathUtils.clamp(dayDom ? cfg.daylight : cfg.moonIntensity, 0, 1);
      const gCol = (dayDom ? _sunGlint : _moonGlint).clone().multiplyScalar(gI);
      this._water.setSunDirection(dir);
      this._water.setColors(cfg.waterColor, gCol);
    }
    // Quantitat de llum → exposició (mai negre; la lluna manté el terra il·luminat).
    this._exposure = cfg.exposure;
    this._compositor?.setExposure(this._exposure);
    // Temperatura del color: albada/posta càlides, nit freda (corba pròpia → matisos per hora).
    this._compositor?.setTemperature(cfg.warmth);
    // A albada/posta afluixem el grade perquè surtin els taronges/vermells naturals (warmth² →
    // el migdia gairebé no s'afluixa, només els pics càlids de sortida/posta).
    this._compositor?.setBrandHour(1 - BRAND_SETTINGS.naturalPull * cfg.warmth * cfg.warmth);
  }

  // ── Overrides d'ambient en viu (debug). ULL: moure el slider d'hora torna a aplicar
  // el preset horari i sobreescriu aquests overrides — serveixen per TROBAR valors que
  // després es cuinen a la taula ANCHORS de TimeOfDay. ─────────────────────────────
  static setExposure(v) { this._exposure = v; this._compositor?.setExposure(v); }
  static setSkyUniform(name, v) { if (this._sky) this._sky.material.uniforms[name].value = v; }
  static setMoonIntensity(v) { this._moonBody?.setIntensity(v); }
  static setSunElevation(deg) {
    this._elevation = deg;
    const phi = MathUtils.degToRad(90 - deg);
    const theta = MathUtils.degToRad(this._azimuth ?? 180);
    this._sunDir.setFromSphericalCoords(1, phi, theta);
    if (this._sky) this._sky.material.uniforms.sunPosition.value.copy(this._sunDir);
    if (this._water) this._water.setSunDirection(this._sunDir);
    if (this._moon) this._moon.position.copy(this._sunDir).multiplyScalar(8000);
  }

  // Split del double render: 0 = superfície/mar · 1 = underwater/2n render (endpoints NETS, sense glitch
  // de parallax). NO fer servir >1 (el parallax desplaça la textura fora de límits → estirament).
  // ── Transició del sea (split): TARGET + LERP per frame (robust; res de tweens que es puguin
  // interrompre/reiniciar en parar i tornar a fer scroll). `_split` = valor actual, `_splitTarget` =
  // objectiu; el loop fa lerp de _split cap al target. show/hide només fixen el target (idempotent):
  //   showRender → 0 (superfície entra) · hideRender → 1 (superfície surt → underwater)
  //   directShowRender → 0 instantani (1r cop). setSplit(v) = instant (slider debug).
  static _split = 1;
  static _splitTarget = 1;
  static _applySplit(v) { this._split = v; this._compositor?.setSplit(v); }
  static setSplit(v) { this._splitTarget = v; this._applySplit(v); }  // instant (target + actual)
  static setSplitTarget(v) { this._splitTarget = v; }                  // el loop hi fa lerp
  static showRender() { this.setSplitTarget(0); }
  static hideRender() { this.setSplitTarget(1); }
  static directShowRender() { this.setSplit(0); }

  // ── Animació d'entrada del sea (framing + mar), SEPARADA en SETUP i PLAY ──────────────────────────
  // El SETUP fixa l'estat INICIAL (des d'on s'anima) i el PLAY llança el tween. Cal separar-los perquè
  // el setup s'ha de fer a _sceneBeforeShow (abans de revelar la pàgina) i el play a afterShow (quan es
  // revela): si no, el "set inicial" i el tween van junts i, si no crides res, el mar es queda a repòs.

  // FRAMING: fixa l'estat inicial I arrenca el tween ja al setup (_sceneBeforeShow),
  // perquè al revelar la pàgina l'animació ja porti recorregut (no arrenca "de cop").
  static setupIntroFraming() {
    RENDER_SETTINGS.seaFramingY = INTRO_TIMING.framing.from;
    RENDER_SETTINGS.surfaceFade = SURFACE_FADE.from;
    this.playIntroFraming();
  }
  static playIntroFraming() {
    const t = INTRO_TIMING.framing;
    this._framingTween?.kill();
    this._framingTween = gsap.to(RENDER_SETTINGS, { seaFramingY: t.to, duration: t.duration, delay: t.delay, ease: t.ease });
    this._surfaceTween?.kill();
    this._surfaceTween = gsap.to(RENDER_SETTINGS, { surfaceFade: 1, duration: SURFACE_FADE.duration, ease: SURFACE_FADE.ease });
  }

  // MAR (settings): arrenca a SEA_INTRO_FROM i transiciona cap al repòs. El mar no llegeix els settings
  // cada frame → apliquem via setConfig a l'onUpdate. NOTA: `spread` refà la geometria a cada frame (car).
  // Només amb USE_LEGACY_SEA_ANIM (si no, la intro és només reframe Y).

  // static setupIntroSea() {
  //   if (!USE_LEGACY_SEA_ANIM) return;

  //   Object.assign(WATER_SETTINGS, SEA_INTRO_FROM);
  //   this._water?.setConfig(WATER_SETTINGS);

  //   gsap.to(WATER_SETTINGS, {
  //     ...WATER_SETTINGS_FINAL,
  //     duration: ...,
  //     ease: ...,
  //     onUpdate: () => this._water?.setConfig(WATER_SETTINGS),
  //   });
  // }

  // static setupIntroSea() {
  //   if (!USE_LEGACY_SEA_ANIM) return;

  //   console.log('SET UP INTRO SEA');

  //   const keys = Object.keys(SEA_INTRO_FROM);
  //   this._seaIntroTo = {};
  //   keys.forEach((k) => { this._seaIntroTo[k] = WATER_SETTINGS[k]; });

  //   Object.assign(WATER_SETTINGS, SEA_INTRO_FROM);
  //   this._water?.setConfig(WATER_SETTINGS);
  // }

  // MAR: sin animación de entrada — arranca ya en el estado FINAL de reposo
  // (mismo que acaba). El framing (seaFramingY) sigue animando aparte.
  static setupIntroSea() {
    if (!USE_LEGACY_SEA_ANIM) return;

    const to = (window.innerHeight > window.innerWidth)
      ? WATER_SETTINGS_PORTRAIT
      : WATER_SETTINGS_DESKTOP;

    this._seaTween?.kill();
    Object.assign(WATER_SETTINGS, to);
    this._water?.setConfig(WATER_SETTINGS);
  }

  // static playIntroSea() {
  //   if (!USE_LEGACY_SEA_ANIM) return; // sense settings de mar (INTRO_TIMING_SIMPLE no té `sea`)

  //   const t = INTRO_TIMING.sea;
  //   this._seaTween?.kill();
  //   this._seaTween = gsap.to(WATER_SETTINGS, {
  //     ...(this._seaIntroTo || SEA_INTRO_TO), duration: t.duration, delay: t.delay, ease: t.ease,
  //     onUpdate: () => {
  //       this._water?.setConfig(WATER_SETTINGS)
  //       console.log(this._water.water.position.y);
  //     },
  //   });
  // }

  // Combinats: SETUP (estat inicial) i PLAY (tweens) de tota l'entrada de la Home.
  static setupIntroHome() { this.setupIntroFraming(); this.setupIntroSea(); }
  static playIntroHome() { this.playIntroFraming(); /* this.playIntroSea(); */ }

  // Compat / botó del pane "animate intro": setup ja arrenca el framing.
  static introHome() { this.setupIntroHome(); }

  // Aplica INSTANTÀNIAMENT l'estat FINAL de l'entrada (settings de repòs del mar + reenquadrament final),
  // sense animació. Per entrar per About/Legal (init): el mar queda directament al seu estat final, sense
  // reproduir l'entrada animada de la Home. Mata qualsevol tween d'entrada en curs.
  static directShowSea() {
    this._seaTween?.kill();
    this._framingTween?.kill();

    Object.assign(
      WATER_SETTINGS,
      isPortrait
        ? WATER_SETTINGS_PORTRAIT
        : WATER_SETTINGS_DESKTOP
    );

    // Object.assign(WATER_SETTINGS, SEA_INTRO_TO);   // settings del mar → repòs (final)

    this._water?.setConfig(WATER_SETTINGS);
    RENDER_SETTINGS.seaFramingY = INTRO_TIMING.framing.to; // reenquadrament → final
    RENDER_SETTINGS.seaFramingYScroll = 0;         // sense move del billboard (About/Legal no en tenen)
    this._water?.setScrollY(0);
  }

  // MOVE del billboard: porta el mar des dels valors POST-INTRO fins als targets segons el progrés.
  // A mobile/portrait el billboard NO reposa a progress 0.5 (com desktop) → capturem el progrés INICIAL
  // (rest, la 1a onMove) i el fem servir com a `start` (t=0). Així el move parteix SEMPRE del repòs, sigui
  // quin sigui el rang de progrés del dispositiu. `end` (de SEA_SCROLL) marca on s'assoleix el target.
  static setSeaScroll(progress) {
    if (this._seaProgressStart === null) this._seaProgressStart = progress;

    const s = SEA_SCROLL;
    const denom = Math.max(0.0001, s.end - this._seaProgressStart); // evita /0 si start≈end
    const t = Math.min(1, Math.max(0, (progress - this._seaProgressStart) / denom)); // 0 al repòs → 1 a `end`

    RENDER_SETTINGS.seaFramingYScroll = (s.framingTarget - RENDER_SETTINGS.seaFramingY) * t;

    const baseSeaY = isPortrait ? WATER_SETTINGS_PORTRAIT.seaY : WATER_SETTINGS_DESKTOP.seaY;
    this._water?.setScrollY((s.seaYTarget - baseSeaY) * t);
  }

  // Reset del punt de partida del move → re-captura el progrés de repòs a la següent onMove. El crida el
  // billboard en crear-se (nova pàgina / canvi de layout o orientació: el rang de progrés canvia).
  static resetSeaScroll() { this._seaProgressStart = null; }

  // Items (layer + intensitat) que contribueixen al bloom, segons els flags/intensitats del pane.
  static _bloomItems() {
    const b = BLOOM_SETTINGS, items = [];
    if (b.particles) items.push({ layer: PARTICLE_LAYER, intensity: b.particlesIntensity });
    if (b.godrays) items.push({ layer: GODRAY_LAYER, intensity: b.godraysIntensity });
    if (b.lightcircles) items.push({ layer: LIGHTCIRCLE_LAYER, intensity: b.lightcirclesIntensity });
    // Lluernes: intensitat pròpia (FIREFLY_SETTINGS.glowIntensity), no cal cap flag nou a
    // BLOOM_SETTINGS — es filtren igualment per intensity>0 a SceneCompositor.render().
    if (this._fireflies) items.push({ layer: FIREFLY_LAYER, intensity: FIREFLY_SETTINGS.glowIntensity });
    return items;
  }

  // ── Loop control ─────────────────────────────────────────────────────────────
  static start() {
    if (!this._initialized || this._running) return;
    this._compositor?.setExposure(this._exposure);
    WebGLSketch.disableRender(); // prenem el control del render a pantalla (double render)
    if (this._sky) this._sky.visible = true;
    this._water?.show();
    this.setSplit(1); // arrenca a UNDERWATER; el sea es mostra amb showRender/directShowRender
    this._lastT = performance.now();
    this._running = true;
  }

  static stop() {
    this._running = false;
    WebGLSketch.enableRender();
  }

  // ── Show / Hide del RENDER (OPACITAT del canvas) ─────────────────────────────
  // Visibilitat a nivell de PÀGINA (fade 0.3s del canvas #scene-three): hide en anar a una pàgina SENSE
  // escena (ho fa Default); show en tornar a home/about quan NO és el 1r cop (ho fan Home/About).
  // Al acabar el fade-out de hide() el loop queda PAUSADO (`_paused`: sense update ni render, 0 GPU a pàgines
  // sense escena); show() el reprèn abans del fade-in. Separat de `_running` perquè start() no s'ha de
  // tornar a executar (resetejaria el split).
  static _paused = false;

  static show() {
    this._paused = false;
    this._lastT = performance.now(); // evita un dt gran al primer frame després de la pausa
    const el = WebGLSketch.domElement;
    if (el) gsap.to(el, { opacity: 1, duration: 0.3, ease: 'none', overwrite: true });
  }

  static hide() {
    const el = WebGLSketch.domElement;
    if (el) gsap.to(el, { opacity: 0, duration: 0.3, ease: 'none', overwrite: true, onComplete: () => { this._paused = true; } });
    else this._paused = true;
  }

  // ── Frame ────────────────────────────────────────────────────────────────────
  // Renderitza SEMPRE que l'escena està inicialitzada (independent de l'scroll/scroll-items).
  static loop() {
    if (!this._running || this._paused || !this._compositor) return;
    const now = performance.now();
    const dt = this._lastT ? Math.min((now - this._lastT) / 1000, 0.05) : 0.016;
    this._lastT = now;

    // Lerp robust del split cap al target (frame-rate independent). Un canvi de target no reinicia
    // res: _split segueix avançant suaument. Snap quan és a prop per fer 0/1 exactes (skip de passos).
    if (Math.abs(this._splitTarget - this._split) > 0.0005) {
      const k = 1 - Math.exp(-6.5 * dt);           // ~0.25s de constant de temps; puja per anar més ràpid
      let v = this._split + (this._splitTarget - this._split) * k;
      if (Math.abs(this._splitTarget - v) <= 0.0005) v = this._splitTarget;
      this._applySplit(v);
    }

    // Salta la feina (loop + render pass) de la regió que NO es veu segons el split. IMPORTANT: durant
    // TOTA la transició els dos passos han de renderitzar-se; només se'n salta un EN REPÒS (split 0/1
    // exacte). Si no, prop de split=1 el composite mostra una franja de mar amb la rtTop CONGELADA (onades
    // que "salten"). El lerp fa snap a 0/1 (a ±0.0005), així que el llindar va per sobre d'aquest punt.
    const showTop = this._split < 0.9995;     // superfície (mar/cel) visible (salta NOMÉS a repòs split=1)
    const showBottom = this._split > 0.0005;  // underwater visible (salta NOMÉS a repòs split=0)

    if (showTop) {
      this._water?.loop();
      this._stars?.loop(dt);
      this._clouds?.loop(dt);
      this._haze?.loop(dt);
    }
    if (showBottom) {
      this._placeSphere();
      this._liquid?.loop(dt);
      this._fireflies?.loop(dt);
      this._particles?.loop(dt);
      for (let i = 0; i < this._godrays.length; i++) this._godrays[i].loop(dt);
      for (let i = 0; i < this._lightCircles.length; i++) this._lightCircles[i].loop(dt);
      this._seabed?.loop(dt);
    }

    // Double render: mar+cel (dalt) / esfera + partícules + godrays + cercles de llum (baix).
    this._compositor?.render(
      WebGLSketch.renderer, WebGLSketch.scene, WebGLSketch.camera,
      [SEA_LAYER], [SPHERE_LAYER, PARTICLE_LAYER, GODRAY_LAYER, LIGHTCIRCLE_LAYER, FIREFLY_LAYER, SEABED_LAYER],
      showTop, showBottom,
      this._bloomItems() // bloom: subconjunt + intensitat per tipus (particles/godrays/lightcircles)
    );
  }

  // ── Godrays (fake, per bloc) — al 2n render. Els crea/destrueix l'scroll-item del bloc; aquí només
  // els registrem perquè el loop els animi/reposicioni i el compositor els renderitzi. ──
  static addGodrays(g) {
    if (!g) return;
    g.setLayer(GODRAY_LAYER);
    this.group.add(g);
    this._godrays.push(g);
  }
  static removeGodrays(g) {
    const i = this._godrays.indexOf(g);
    if (i !== -1) this._godrays.splice(i, 1);
    this.group.remove(g);
    g.dispose();
  }

  // ── Cercles de llum (block_step) — mateix flux que els godrays, al 2n render. ──
  static addLightCircle(c) {
    if (!c) return;
    c.setLayer(LIGHTCIRCLE_LAYER);
    this.group.add(c);
    this._lightCircles.push(c);
  }
  static removeLightCircle(c) {
    const i = this._lightCircles.indexOf(c);
    if (i !== -1) this._lightCircles.splice(i, 1);
    this.group.remove(c);
    c.dispose();
  }

  // ── Fons marí (footer) — al 2n render. El crea/destrueix l'scroll-item del footer; aquí només el
  // registrem perquè el loop l'animi i el compositor el renderitzi. Un de sol a la vegada. ──
  static addSeabed(s) {
    if (!s) return;
    if (this._seabed) this.removeSeabed(this._seabed);
    s.setLayer(SEABED_LAYER);
    this.group.add(s.group);
    this._seabed = s;
  }
  static removeSeabed(s) {
    if (!this._seabed || (s && s !== this._seabed)) return;
    this.group.remove(this._seabed.group);
    this._seabed.dispose();
    this._seabed = null;
  }

  // ── Esfera de líquid + lluernes (2n render) — la crea/destrueix l'scroll-item del bloc que té el
  // target (block_particles_fake → .ball). Es col·loca cada frame al centre del target. Una sola
  // esfera a la vegada: un addSphere nou substitueix l'anterior. ──
  // viewportCenter: la Y es calcula com a top del target SENSE el seu transform + mitja pantalla → quan el
  // target és sticky a dalt, l'esfera queda EXACTAMENT al centre (ignora parallax/yPercent del target i la
  // diferència 100vh ≠ innerHeight de la barra d'adreces); abans/després entra i surt amb el bloc.
  static addSphere(target, { viewportCenter = false } = {}) {
    if (!target || !this._initialized) return;
    if (this._liquid) this.removeSphere(this._sphereTarget);

    this._sphereTarget = target;
    this._sphereViewportCenter = viewportCenter;
    this._liquid = new LiquidSphere();
    this.group.add(this._liquid.init(WebGLSketch.renderer));
    this._liquid.setLayer(SPHERE_LAYER);

    // Layer pròpia (no SPHERE_LAYER): les lluernes participen soles al bloom (_bloomItems()).
    this._fireflies = new Fireflies();
    this.group.add(this._fireflies.init());
    this._fireflies.setLayer(FIREFLY_LAYER);

    this._placeSphere();
    if (isDebug) DebugPane.mountSphere(this);
  }

  // `target` evita que el dispose d'un bloc antic tregui l'esfera d'un bloc més nou.
  static removeSphere(target) {
    if (!this._liquid || (target && target !== this._sphereTarget)) return;
    if (isDebug) DebugPane.unmountSphere();
    this.group.remove(this._liquid.group);
    this._liquid.dispose();
    this._liquid = null;
    if (this._fireflies) {
      this.group.remove(this._fireflies.group);
      this._fireflies.dispose();
      this._fireflies = null;
    }
    this._sphereTarget = null;
  }

  // Radi animat de l'esfera (fracció × HEIGHT); null → valor de LIQUID_SETTINGS.
  static setSphereRadius(value) {
    this._liquid?.setRadius(value);
  }

  // Posició respecte al CANVAS i la seva càmera (camera2D: la càmera veu WebGLSketch.size unitats de món
  // sobre la mida CSS del canvas), NO respecte a Metrics: a mobile Metrics pot no coincidir amb la càmera
  // (WebGLSketch.resize no fa res a smartphone; a l'emulació de DevTools Metrics arrenca amb 1560×3376).
  static _placeSphere() {
    if (!this._sphereTarget || !this._liquid?.group) return;
    const c = WebGLSketch.domElement.getBoundingClientRect();
    if (!c.width || !c.height) return;
    const kx = WebGLSketch.size.x / c.width;
    const ky = WebGLSketch.size.y / c.height;
    const r = this._sphereTarget.getBoundingClientRect();
    const screenX = r.left + r.width * 0.5;
    let screenY = r.top + r.height * 0.5;
    if (this._sphereViewportCenter) {
      const ty = new DOMMatrixReadOnly(getComputedStyle(this._sphereTarget).transform).m42;
      screenY = r.top - ty + c.height * 0.5;
    }
    const x = (screenX - (c.left + c.width * 0.5)) * kx;
    const y = ((c.top + c.height * 0.5) - screenY) * ky;
    this._liquid.group.position.set(x, y, 0);
    this._fireflies?.group?.position.set(x, y, 0);
  }

  static resize() {
    this._compositor?.resize(WebGLSketch.size.x, WebGLSketch.size.y);
    this._water?.resize();
    this._liquid?.resize();
    this._fireflies?.resize();
    this._placeSphere();
    this._particles?.resize(WebGLSketch.size.y);
    this._stars?.resize();
    this._clouds?.resize();
    this._haze?.resize();
    for (let i = 0; i < this._godrays.length; i++) this._godrays[i].resize();
    for (let i = 0; i < this._lightCircles.length; i++) this._lightCircles[i].resize();
    this._seabed?.resize();
  }

  static dispose() {
    this.stop();
    if (isDebug) DebugPane.unmountScene();
    if (this._particles) { this.group.remove(this._particles.points); this._particles.dispose(); this._particles = null; }
    if (this._stars) { this.group.remove(this._stars.points); this._stars.dispose(); this._stars = null; }
    if (this._clouds) { this.group.remove(this._clouds.mesh); this._clouds.dispose(); this._clouds = null; }
    if (this._haze) { this.group.remove(this._haze.mesh); this._haze.dispose(); this._haze = null; }
    this.removeSphere();
    this.removeSeabed();
    if (this._water) { this.group.remove(this._water.water); this._water.dispose(); this._water = null; }
    if (this._sky) { this.group.remove(this._sky); this._sky.material?.dispose(); this._sky.geometry?.dispose(); this._sky = null; }
    if (this._moon) { this.group.remove(this._moon.target); this.group.remove(this._moon); this._moon = null; }
    if (this._sunBody) { this.group.remove(this._sunBody.mesh); this._sunBody.dispose(); this._sunBody = null; }
    if (this._moonBody) { this.group.remove(this._moonBody.mesh); this._moonBody.dispose(); this._moonBody = null; }
    if (this._compositor) { this._compositor.dispose(); this._compositor = null; }
    WebGLSketch.scene.remove(this.group);
    this._initialized = false;
    this._paused = false;
  }
}
